/**
 * Linha do tempo da máquina de estados do pedido + próximos passos (Task 12).
 *
 * Duas responsabilidades, e nenhuma delas é "decidir a regra":
 *
 * 1. **Mostrar o histórico**: cada transição registrada (`platform_order_events`,
 *    migration 0031) com estado de origem/destino, QUEM registrou, quando, o
 *    motivo e o dado que a justificou (referência de pagamento, NF, rastreio).
 *    Transição espelhada de um registro de origem aparece como `system` — a
 *    tela não a apresenta como ação de operador.
 * 2. **Oferecer o que a máquina permite**: os destinos e os campos exigidos
 *    vêm de `nextStates`/`requirementsFor` (módulo puro), não de uma lista de
 *    botões mantida aqui. Estado final não oferece ação.
 *
 * Como na edição comercial da Task 11, o caminho de gravação entra por prop
 * (`transition`): sem ele a tela é leitura e DIZ que a gravação não está
 * montada, em vez de exibir um botão que não grava.
 *
 * Migrado para `.tsx`/JSX real (Cluster A — Vendas, redesign por perfil; ver
 * `docs/architecture/ADR-0011-*.md`). Só a MOLDURA visual (sintaxe
 * `createElement` → JSX) muda — a lógica de transição (`submitOrderTransition`,
 * `checkTransition`), todos os `data-*`/aria e o texto ficam exatamente como
 * estavam (contrato travado pelo brief do cluster).
 */
// Import explícito de `React` — ver o mesmo comentário em `Margens.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { Circle } from "lucide-react";
import { StatusPanel } from "./StatusPanel.js";
import {
  ORDER_STATE_LABELS,
  TRANSITION_FIELD_LABELS,
  checkTransition,
  isTerminalState,
  requirementsFor,
  type OrderTransitionField,
  type OrderTransitionInput,
  type PlatformOrderState,
  type TransitionCheck
} from "../orders/state-machine.js";
import { availableTransitions, orderTimeline } from "../orders/dataset.js";
import { formatDateTime } from "../format.js";
import type { OrderTransitionHandle } from "../orders/view-props.js";
import type { PlatformUnifiedOrder } from "../api/types.js";

export interface OrderStateMachineProps {
  order: PlatformUnifiedOrder;
  transition?: OrderTransitionHandle;
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface OrderTransitionFormValues {
  get(name: string): unknown;
}

export type OrderTransitionSubmission =
  | { ok: true; input: { to: PlatformOrderState } & OrderTransitionInput }
  | { ok: false; check: TransitionCheck };

/**
 * O caminho de submit REAL da transição, isolado do evento de DOM (que não
 * existe sem navegador): `onSubmit` abaixo só traduz o evento para cá.
 *
 * A validação usa a MESMA `checkTransition` do servidor. Isso não substitui a
 * checagem server-side (a autoridade é o servidor); evita mandar uma transição
 * que já se sabe inválida e diz ao operador, na hora, qual campo falta.
 *
 * O ator não é montado aqui: quem registrou vem da sessão no servidor.
 */
export function submitOrderTransition(
  order: PlatformUnifiedOrder,
  to: PlatformOrderState,
  values: OrderTransitionFormValues,
  transition: OrderTransitionHandle
): OrderTransitionSubmission {
  const input: OrderTransitionInput = {};
  for (const field of requirementsFor(to)) {
    const raw = values.get(field);
    if (typeof raw === "string") input[field] = raw.trim();
  }
  const check = checkTransition(order.state, to, input);
  if (!check.ok) return { ok: false, check };
  const payload = { to, ...input };
  transition.submit(payload);
  return { ok: true, input: payload };
}

// Redesign R6 (FEAT-002, apresentação apenas): a máquina de estados segue o
// padrão aprovado — timeline como lista de itens em cartão tokenizado e o
// formulário de transição com rótulo associado (htmlFor/id), input tokenizado
// e CTA primário vermelho único (bg-brand). PRESERVA o contrato de submit
// (submitOrderTransition), todos os data-*/aria e o vocabulário ORDER_STATE_LABELS.
const FIELD_INPUT_CLASS =
  "rounded-control border border-border bg-surface px-3 py-2 text-sm text-ink " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60";

function FieldInput({ to, field, disabled }: { to: PlatformOrderState; field: OrderTransitionField; disabled: boolean }): ReactElement {
  const id = `transition-${to}-${field}`;
  return (
    <p className="order-transition__field flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-extrabold text-ink">
        {TRANSITION_FIELD_LABELS[field]}
      </label>
      <input id={id} name={field} type="text" required disabled={disabled} data-transition-field={field} className={FIELD_INPUT_CLASS} />
    </p>
  );
}

function TransitionForm({
  order,
  to,
  transition
}: {
  order: PlatformUnifiedOrder;
  to: PlatformOrderState;
  transition: OrderTransitionHandle;
}): ReactElement {
  const required = requirementsFor(to);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submitOrderTransition(order, to, new FormData(event.currentTarget), transition);
  }

  return (
    <form
      className="order-transition flex flex-col gap-3 rounded-panel border border-border bg-surface-soft p-4"
      data-transition-to={to}
      onSubmit={onSubmit}
    >
      <h4 className="text-base font-extrabold text-ink">{`Avançar para: ${ORDER_STATE_LABELS[to]}`}</h4>
      {required.length ? (
        <div className="order-transition__requirements flex flex-col gap-3" data-transition-requires={required.join(",")}>
          {required.map((field) => (
            <FieldInput key={field} to={to} field={field} disabled={transition.pending} />
          ))}
        </div>
      ) : (
        <p className="order-transition__requirements text-sm text-muted" data-transition-requires="">
          Nenhum dado adicional exigido.
        </p>
      )}
      <button
        type="submit"
        disabled={transition.pending}
        data-action={`transition-${to}`}
        className={
          "inline-flex w-fit items-center justify-center rounded-control bg-brand px-4 py-2 text-sm font-semibold text-on-brand " +
          "hover:bg-brand-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60"
        }
      >
        {transition.pending ? "Registrando…" : `Registrar ${ORDER_STATE_LABELS[to].toLowerCase()}`}
      </button>
    </form>
  );
}

/** Dado que justificou a transição, exibido em texto (nunca escondido). */
function payloadSummary(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!entries.length) return "sem dado adicional";
  return entries
    .map(([key, value]) => `${TRANSITION_FIELD_LABELS[key as OrderTransitionField] ?? key}: ${String(value)}`)
    .join(" · ");
}

export function OrderStateMachine({ order, transition }: OrderStateMachineProps): ReactElement {
  const timeline = orderTimeline(order);
  const transitions = availableTransitions(order);
  return (
    <section
      className="order-state-machine flex flex-col gap-4 rounded-panel border border-border bg-surface p-5 shadow-s1"
      data-section="order-state-machine"
    >
      <h3 className="text-lg font-extrabold text-ink">Histórico de estados</h3>
      <p className="page__note text-sm text-muted" data-order-state={order.state} data-order-events={timeline.length}>
        {`Estado atual: ${ORDER_STATE_LABELS[order.state]}. ${order.stateNote}`}
      </p>
      {timeline.length ? (
        <ol className="order-timeline m-0 flex list-none flex-col p-0">
          {timeline.map((event, index) => (
            <li
              key={event.id}
              className="order-timeline__item relative flex gap-3 pb-4 last:pb-0"
              data-record-id={event.id}
              data-order-event-to={event.toState}
              data-order-event-actor-kind={event.actorKind}
            >
              {/* Trilho: ponto marcador + linha conectando ao próximo item. */}
              <span className="relative flex w-4 shrink-0 flex-col items-center" aria-hidden>
                <Circle className="mt-0.5 h-3 w-3 shrink-0 fill-brand text-brand" />
                {index < timeline.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
              </span>
              <p className="order-timeline__content m-0 flex-1 rounded-control bg-surface-soft p-3 text-sm text-body">
                <span className="order-timeline__states">
                  {event.fromState
                    ? `${ORDER_STATE_LABELS[event.fromState]} → ${ORDER_STATE_LABELS[event.toState]}`
                    : `Pedido registrado em ${ORDER_STATE_LABELS[event.toState]}`}
                </span>
                {" · "}
                <span className="order-timeline__actor">
                  {event.actorKind === "staff" ? `registrado por ${event.actor}` : `espelhado do registro de origem (${event.actor})`}
                </span>
                {" · "}
                <time dateTime={event.occurredAt}>{formatDateTime(event.occurredAt)}</time>
                {event.reason ? <span className="order-timeline__reason">{` · motivo: ${event.reason}`}</span> : null}
                <span className="order-timeline__payload">{` · ${payloadSummary(event.payload)}`}</span>
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <StatusPanel
          kind="empty"
          title="Nenhuma transição registrada para este pedido"
          detail="O pedido existe, mas ainda não tem evento na linha do tempo — nada foi inferido no lugar do histórico."
        />
      )}
      <h3 className="text-lg font-extrabold text-ink">Próximos passos</h3>
      {isTerminalState(order.state) ? (
        <StatusPanel
          kind="empty"
          title={`Estado final: ${ORDER_STATE_LABELS[order.state]}`}
          detail="Nenhuma transição sai deste estado. Um novo atendimento (devolução/garantia) abre o seu próprio registro."
        />
      ) : transition ? (
        <div className="order-state-machine__transitions grid grid-cols-1 gap-4 md:grid-cols-2">
          {transitions.map((option) => (
            <TransitionForm key={option.to} order={order} to={option.to} transition={transition} />
          ))}
        </div>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Gravação de transição não montada nesta visualização"
          detail={`Desta etapa o pedido pode seguir para: ${transitions.map((option) => ORDER_STATE_LABELS[option.to]).join(", ")}. Esta montagem é só leitura.`}
        />
      )}
      {transition?.error ? <StatusPanel kind="error" title="A transição não foi registrada" detail={transition.error.message} /> : null}
      {transition?.savedState ? (
        <StatusPanel kind="empty" title={`Transição registrada: ${ORDER_STATE_LABELS[transition.savedState]}`} />
      ) : null}
    </section>
  );
}
